import React, { useRef, useState } from 'react';
import { api } from '../../api/client';
import Logo from '../Logo';
import '../../styles/admin.css';

import { ta } from '../../i18n/adminT';
export default function LicenseSetup({ me, licenseStatus, onActivated, onLogout }) {
  const [error, setError] = useState('');
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  const [showAdvanced, setShowAdvanced] = useState(false);
  const [advancedPassword, setAdvancedPassword] = useState('');
  const [keyError, setKeyError] = useState('');
  const [keyNote, setKeyNote] = useState('');
  const [keyUploading, setKeyUploading] = useState(false);
  const keyFileInputRef = useRef(null);

  const [idCopied, setIdCopied] = useState(false);

  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setError('');
    setUploading(true);
    try {
      await api.uploadLicense(file);
      onActivated();
    } catch (err) {
      setError(err.message);
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleCopyId = async () => {
    if (!licenseStatus?.installId) return;
    try {
      await navigator.clipboard.writeText(licenseStatus.installId);
      setIdCopied(true);
      setTimeout(() => setIdCopied(false), 2000);
    } catch {
      // se il clipboard non è disponibile, l'ID resta comunque selezionabile a mano
    }
  };

  const handleKeyFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!advancedPassword) {
      setKeyError(ta('Inserisci prima la password delle opzioni avanzate.'));
      if (keyFileInputRef.current) keyFileInputRef.current.value = '';
      return;
    }

    setKeyError('');
    setKeyNote('');
    setKeyUploading(true);
    try {
      const res = await api.uploadPublicKey(file, advancedPassword);
      setKeyNote(ta(res.note || 'Chiave pubblica sostituita.'));
      setAdvancedPassword('');
      if (res.licenseValidAfterChange) onActivated();
    } catch (err) {
      setKeyError(err.message);
    } finally {
      setKeyUploading(false);
      if (keyFileInputRef.current) keyFileInputRef.current.value = '';
    }
  };

  return (
    <div className="auth-shell">
      <div className="auth-card" style={{ maxWidth: 460 }}>
        <div style={{ marginBottom: 18 }}><Logo size={40} /></div>
        <h2>{ta('Attivazione richiesta')}</h2>
        <p className="modal-subtitle">
          {ta('Questa installazione non ha ancora una licenza valida.')} {me.isSuperAdmin
            ? ta('Carica qui sotto il file di licenza ricevuto dal fornitore per attivarla.')
            : ta('Contatta un super-amministratore per caricare il file di licenza.')}
        </p>

        {licenseStatus?.reason && (
          <p style={{ fontSize: '0.78rem', color: 'var(--muted-text)', marginBottom: 12 }}>
            {ta('Dettaglio:')} {licenseStatus.reason}
          </p>
        )}

        {licenseStatus?.installId && (
          <div style={{ marginBottom: 20 }}>
            <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 600, marginBottom: 6 }}>
              {ta('ID di installazione di questo dispositivo')}
            </label>
            <div style={{ display: 'flex', gap: 8 }}>
              <input
                type="text"
                readOnly
                value={licenseStatus.installId}
                onFocus={(e) => e.target.select()}
                className="mono"
                style={{ flex: 1, padding: '10px 12px', borderRadius: 'var(--radius-control)', border: '1.5px solid var(--border-hair)', fontSize: '0.82rem', background: 'var(--paper-dim)' }}
              />
              <button type="button" className="btn btn-ghost" onClick={handleCopyId} style={{ width: 'auto', padding: '0 16px', marginTop: 0 }}>
                {idCopied ? ta('Copiato ✓') : ta('Copia')}
              </button>
            </div>
            <p style={{ fontSize: '0.75rem', color: 'var(--muted-text)', marginTop: 6 }}>
              {ta('Comunica questo ID al fornitore per ricevere una licenza valida per questo dispositivo.')}
            </p>
          </div>
        )}

        {error && <div className="form-error">{error}</div>}

        {me.isSuperAdmin ? (
          <div className="upload-drop">
            <label htmlFor="license-file">{uploading ? ta('Verifica in corso…') : ta('Carica file di licenza (.lic)')}</label>
            <input
              id="license-file"
              ref={fileInputRef}
              type="file"
              onChange={handleFileChange}
              disabled={uploading}
            />
          </div>
        ) : null}

        {me.isSuperAdmin && (
          <>
            <button
              type="button"
              className="doc-link-btn"
              style={{ display: 'block', margin: '18px auto 0' }}
              onClick={() => setShowAdvanced((v) => !v)}
            >
              {showAdvanced ? ta('Nascondi opzioni avanzate') : ta('Opzioni avanzate')}
            </button>

            {showAdvanced && (
              <div style={{ marginTop: 14, padding: 14, border: '1.5px dashed var(--border-hair)', borderRadius: 'var(--radius-control)' }}>
                <p style={{ fontSize: '0.78rem', color: 'var(--clay-red-dark)', marginBottom: 12, fontWeight: 600 }}>
                  {ta('⚠ Sostituisci la chiave pubblica solo se sai per certo che quella attuale non corrisponde alla coppia di chiavi del fornitore. Chi controlla questa chiave può generare licenze valide per questo dispositivo: caricala solo da un file di cui ti fidi.')}
                </p>
                {keyError && <div className="form-error">{keyError}</div>}
                {keyNote && (
                  <div style={{ background: 'rgba(62,142,126,0.12)', color: 'var(--teal-green-dark)', borderRadius: 'var(--radius-control)', padding: '10px 14px', fontSize: '0.8rem', marginBottom: 14 }}>
                    {keyNote}
                  </div>
                )}

                <div className="field">
                  <label htmlFor="advanced-password">{ta('Password opzioni avanzate')}</label>
                  <input
                    id="advanced-password"
                    type="password"
                    value={advancedPassword}
                    onChange={(e) => setAdvancedPassword(e.target.value)}
                    autoComplete="off"
                  />
                </div>

                <div className="upload-drop" style={{ opacity: advancedPassword ? 1 : 0.5 }}>
                  <label htmlFor="public-key-file">{keyUploading ? ta('Caricamento…') : ta('Sostituisci public_key.pem')}</label>
                  <input
                    id="public-key-file"
                    ref={keyFileInputRef}
                    type="file"
                    onChange={handleKeyFileChange}
                    disabled={keyUploading || !advancedPassword}
                  />
                </div>
              </div>
            )}
          </>
        )}

        <button type="button" className="btn btn-ghost" onClick={onLogout} style={{ marginTop: 18 }}>
          {ta('Esci')}
        </button>

        <p className="security-note">{me.username}{me.isSuperAdmin ? ta(' · super-admin') : ''}</p>
      </div>
    </div>
  );
}
